import api from "../api.service"
import apiComments from "../comment/comment.api"
import apiActionItem from "../actionItem/actionItem.api"

/**
 * Chat (API api/chat): direct messages, groups and task rooms in one organisation.
 *
 * Not per organisation: a person chats with the people of their organisations
 * and of their missions. A direct message's or group's messages live at /chat; a task's are
 * the task's own comments (comment.api), so they show on the task as well. Both
 * kinds stream the same frames over /comment/<room>/<roomId>.
 */

/** The comment room of a direct message or group. Mirrors the API's ROOM_MODEL. */
export const CHAT_ROOM = "ChatConversation"

/** The comment room of a task. */
export const TASK_ROOM = "ActionItem"

const unwrap = (res) => res.data
const fail = (err) => {
    throw api.getResponseError(err)
}

class ChatApi {
    /** A person's direct messages and groups, every organisation, latest first. */
    listConversations() {
        return api
            .get(`/chat/conversations`)
            .then((res) => res.data?.value || [])
            .catch(fail)
    }

    /**
     * A direct message with one person (the existing one if any), else a new group.
     *
     * @param people {String[]} The others' ids
     * @param [title] {String} A group's name
     */
    createConversation(people, title) {
        return api.post(`/chat/conversations`, { people, title }).then(unwrap).catch(fail)
    }

    /** Rename a group or add people: { title?, addPeople? } */
    updateConversation(conversationId, data) {
        return api.put(`/chat/conversations/${conversationId}`, data).then(unwrap).catch(fail)
    }

    leaveConversation(conversationId) {
        return api.post(`/chat/conversations/${conversationId}/leave`, {}).then(unwrap).catch(fail)
    }

    /**
     * A page of messages, oldest first.
     *
     * @param [before] {String} A message id: older than it
     * @return {Promise<{value: Object[], hasMore: Boolean}>}
     */
    listMessages(conversationId, before) {
        const qs = before ? `?before=${before}` : ""

        return api
            .get(`/chat/conversations/${conversationId}/messages${qs}`)
            .then(unwrap)
            .catch(fail)
    }

    postMessage(conversationId, data) {
        return api
            .post(`/chat/conversations/${conversationId}/messages`, data)
            .then(unwrap)
            .catch(fail)
    }

    /**
     * Read a conversation. `opened`: they opened it (always stamped); otherwise
     * the server writes nothing when nothing was unread.
     */
    markRead(conversationId, { opened = false } = {}) {
        return api.put(`/chat/conversations/${conversationId}/read`, { opened }).then(unwrap).catch(fail)
    }

    /** A pasted image, stored for the conversation's members. @return {Promise<Object>} the file */
    uploadImage(conversationId, file) {
        return api
            .uploadFileSingle(`/chat/conversations/${conversationId}/images`, file)
            .then(unwrap)
    }

    editMessage(messageId, text) {
        return api.put(`/chat/messages/${messageId}`, { text }).then(unwrap).catch(fail)
    }

    deleteMessage(messageId) {
        return api.delete(`/chat/messages/${messageId}`).then(unwrap).catch(fail)
    }

    clapMessage(messageId) {
        return api.put(`/chat/messages/${messageId}/clap`, {}).then(unwrap).catch(fail)
    }

    /** The task column: { missions, tasks } */
    listTasks() {
        return api.get(`/chat/tasks`).then(unwrap).catch(fail)
    }

    /** Opening a task's room: joins it and reads it. @return {Promise<Object>} the conversation */
    openTask(taskId) {
        return api.put(`/chat/tasks/${taskId}/read`, {}).then(unwrap).catch(fail)
    }

    /** Who can be @mentioned in a task's room: its mission's team. */
    taskPeople(taskId) {
        return api
            .get(`/chat/tasks/${taskId}/people`)
            .then((res) => res.data?.value || [])
            .catch(fail)
    }

    /** The Chats badge: { total, byOrg, conversations } */
    unread() {
        return api.get(`/chat/unread`).then(unwrap).catch(fail)
    }

    /** Everyone a new chat can be with: the people of my organisations and of my missions. */
    people() {
        return api
            .get(`/chat/people`)
            .then((res) => res.data?.value || [])
            .catch(fail)
    }

    /**
     * Follows a person's conversations (lists and badges).
     *
     * @param operation {"update"|"delete"}
     * @param cb {function(Object[])}
     * @param [onReconnect] {function} After a dropped connection comes back: frames were missed
     * @return {{disconnect: function}}
     */
    onConversationChanges(operation, cb, onReconnect) {
        let socket
        let closed = false

        api.socket.connect(`changes/chat.conversations/${operation}`).then((s) => {
            if (closed) {
                s.disconnect()
                return
            }
            socket = s
            socket.on("changes", cb)
            // socket.io says "connect" again on every reconnect
            if (onReconnect) socket.on("connect", onReconnect)
        })

        return {
            disconnect: () => {
                closed = true
                socket?.disconnect()
            },
        }
    }

    /**
     * The room open on screen: its messages as they are posted, edited, clapped, deleted.
     *
     * @param room {String} CHAT_ROOM or TASK_ROOM
     * @param roomId {String} The conversation's or the task's id
     */
    onRoomChanges(room, roomId, cb, onReconnect) {
        let socket
        let closed = false

        api.socket.connect(`comment/${room}/${roomId}`).then((s) => {
            if (closed) {
                s.disconnect()
                return
            }
            socket = s
            socket.on("changes", cb)
            // socket.io says "connect" again on every reconnect: messages were missed
            if (onReconnect) socket.on("connect", onReconnect)
        })

        return {
            disconnect: () => {
                closed = true
                socket?.disconnect()
            },
        }
    }
}

const chatApi = new ChatApi()

export default chatApi

/**
 * One room's messages, whichever kind it is: the thread reads and writes through this.
 *
 * @param room {{kind: String, conversationId?: String, taskId?: String}}
 */
export function roomClient(room) {
    if (room.kind === "task") {
        return {
            socketRoom: TASK_ROOM,
            socketId: room.taskId,
            list: () =>
                apiComments
                    .actionItem(room.taskId)
                    .getCommentList()
                    .then((value) => ({
                        // The task's whole thread at once (that endpoint does not page), oldest first
                        value: (value || []).slice().sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
                        hasMore: false,
                    })),
            post: ({ text, mentions }) => apiComments.actionItem(room.taskId).addComment({ text, mentions }),
            edit: (message, text) => apiComments.updateComment(message._id, { text }),
            remove: (message) => apiComments.deleteComment(message._id),
            clap: (message) => apiComments.clap(message._id),
            uploadImage: (file) => apiActionItem.uploadFiles(room.taskId, file).then((files) => files[files.length - 1]),
        }
    }

    return {
        socketRoom: CHAT_ROOM,
        socketId: room.conversationId,
        list: (before) => chatApi.listMessages(room.conversationId, before),
        post: ({ text, mentions }) => chatApi.postMessage(room.conversationId, { text, mentions }),
        edit: (message, text) => chatApi.editMessage(message._id, text),
        remove: (message) => chatApi.deleteMessage(message._id),
        clap: (message) => chatApi.clapMessage(message._id),
        uploadImage: (file) => chatApi.uploadImage(room.conversationId, file),
    }
}
